import { Controller } from "@hotwired/stimulus"
import { anim, burst, coinFly, floatLabel, tween } from "../helpers/goal_climb_fx"
import { moneyText } from "../helpers/money_text"

// The goal, month by month (goals/_dashboard_chart). Draws the chart from
// monthsValue (GoalMonths, one entry per month):
//
//   the line    the running total at each month's end, a smooth monotone
//               curve (never overshoots between months) with a soft glow
//               under it; a month with a take-out bends it red
//   the ends    a sticker flag where the goal started and a sticker trophy
//               at the goal; both slap on, the trophy cheers now and then
//   milestones  dashed vertical lines where the line crosses 25%, 50% and
//               75% of the goal (done or planned) and at the goal, each
//               labelled on top; a gold shape on the line at each crossing
//               (diamond, star, hexagon), faded while only planned
//   the tiles   one per month: its name over a thin bar split green (in) and
//               red (out) by share; four grey dots for months to come
//   selecting   a tile or dot raises a column to its dot, gives its tile a surface,
//               makes its dot glow and fills the readout pinned top-left
//
// Opens scrolled to this month, which starts selected. A coin pinned to the
// end edge stands in for the goal while it is scrolled out of view. Every
// label is set with textContent. Reduced Motion: no draw-in, no loops.
//
// Money since the last visit (goal_trail's pattern): the chart remembers
// today's total it last showed (localStorage, keyValue). When it comes back
// higher (a set-aside sheet just closed onto this screen), the chart draws
// still at the old total and, once on screen, a coin arcs in, lands on
// today's dot and the dot springs up to the new total, its glow a beat late,
// "+amount" floating up and sparks off any milestone it passes.
const NS = "http://www.w3.org/2000/svg"
const MIN_TILE = 56 // the narrowest a month gets; wider when few months fill the card
const GAP = 16 // between two months' tiles
const INSET = 12 // the card's inner edge
const BASE = 240
const TOP = 44
const TILE_TOP = BASE // the month tiles hang flat from the baseline
const TILE_H = 42 // name 15 / bar 27-31: ~11 above the name, 11 under the bar
const BAR_W = 0.8 // the month bar's share of its full width
const TILE_PAD = 0.7 // the selected surface keeps 70% of the tile's side padding
const BAND_W = 22 // the selected month's column
const CREAM = "#FFF7E6" // the sticker border
const GOLD = { fill: "#FCD34D", shade: "#F5B301", edge: "#92400E", hi: "#FFF7D6" }
const FLAG_H = 42 // the flag's drawn height above its point, sticker border included
const FLAG_SCALE = 0.65 // the start flag, a touch smaller than drawn
const RAIL_Y = 10 // the progress rail's centre line; the milestone labels sit in it
const RAIL_H = 16
const READOUT_GAP = 10 // the least room between the rail and the readout
const CAP_FADE = 16 // the Progress cap's fading end, where a label may still show
const LABEL_HALF = 18 // half a milestone label's width
const LABEL_GAP = 34 // the least room between two milestone labels' centres
const RAIL_LABEL_HALF = 24 // half a milestone label plus room for the fill's end dot
const MARK_TOP = RAIL_Y + RAIL_H / 2 // where the milestone stems start, under the rail
const SHAPE_GAP = 9 // a stem stops this far above its milestone shape
const TROPHY_TOP = 18 // and this far above the goal's centre (the trophy's top)
const MILESTONE_SHARES = [0.25, 0.5, 0.75]
// The new money's arrival (Option A "Coin drop")
const ARRIVE_DELAY_MS = 350 // after the chart comes into view, the sheet settled
const ARRIVE_IN_VIEW = 0.6 // share of the chart on screen before it plays
const COIN_FROM_X = 90 // the coin starts this far before today's dot
const COIN_FROM_Y = 46 // at this height in the chart
const COIN_MS = 760
const SPRING_MS = 1100 // the dot's spring up to the new total
const GLOW_LAG_S = 0.09 // the glow follows the line this late
const springAt = (s) => 1 - Math.exp(-7 * s) * Math.cos(11 * s) // damped, one soft overshoot
const linear = (t) => t

export default class extends Controller {
  static targets = ["scroller"]
  static values = { months: Array, target: Number, labels: Object, key: String, money: Object }

  connect() {
    this.gone = false
    if (!this.monthsValue.length || this.targetValue <= 0) return
    this.nowIndex = Math.max(0, this.monthsValue.findIndex((m) => m.state === "now"))
    // Once the goal is reached the months after today plan money no longer
    // needed: the chart ends on today, the trophy on the month it got there.
    const reached = (this.monthsValue[this.nowIndex]?.total || 0) >= this.targetValue
    this.realMonths = reached ? this.monthsValue.slice(0, this.nowIndex + 1) : this.monthsValue
    const saved = this.realMonths[this.nowIndex]?.total || 0
    const from = this.previousSaved(saved)
    this.arrival = from !== null && from !== saved && !this.reduced ? { from, to: saved, down: from > saved } : null
    if (!this.arrival) this.storeSaved(saved)
    // Only arriving money counts the readout's Set aside up; a take-out
    // leaves Set aside as it was.
    this.insFrom = this.arrival && !this.arrival.down ? Math.max(0, this.realMonths[this.nowIndex].ins - (saved - from)) : null
    // Arriving money: draw still (no entrance) at the old total first.
    this.months = this.arrival ? this.withNowTotal(from) : this.realMonths
    this.element.classList.toggle("is-still", !!this.arrival)
    this.lastWidth = this.scrollerTarget.clientWidth
    // A Turbo cache snapshot still holds the last render: start clean.
    this.clear()
    this.build()
    requestAnimationFrame(() => {
      this.scrollerTarget.scrollLeft = Math.max(0, this.x(this.nowIndex) - this.scrollerTarget.clientWidth * 0.55)
      this.syncPin()
      this.syncCues()
    })
    // One scroll listener per connect (rebuilds do not add more); removed on disconnect.
    this.onScroll = () => { this.syncCues(); this.syncPin(); this.syncCapLabels() }
    this.scrollerTarget.addEventListener("scroll", this.onScroll, { passive: true })
    // The card can be laid out after connect (a hidden carousel panel), so
    // the pin re-checks whenever the strip's size changes.
    this.resizeObserver = new ResizeObserver(() => {
      const width = this.scrollerTarget.clientWidth
      if (width && width !== this.lastWidth) { this.lastWidth = width; this.rebuild() }
      this.syncPin()
      this.syncCues()
    })
    this.resizeObserver.observe(this.scrollerTarget)
    this.selectTimer = setTimeout(() => this.select(this.nowIndex), this.reduced || this.arrival ? 0 : 1500)
    if (this.arrival) this.awaitView()
  }

  disconnect() {
    this.gone = true
    if (this.onScroll) this.scrollerTarget.removeEventListener("scroll", this.onScroll)
    clearTimeout(this.selectTimer)
    clearTimeout(this.arriveTimer)
    this.resizeObserver?.disconnect()
    this.viewObserver?.disconnect()
  }

  // The total this chart last showed for today, when it differs from now;
  // null on a first visit, a changed goal, or a Turbo preview.
  previousSaved(saved) {
    if (!this.keyValue || document.documentElement.hasAttribute("data-turbo-preview")) return null
    try {
      const stored = JSON.parse(window.localStorage.getItem(this.keyValue) || "null")
      if (stored && stored.target === this.targetValue && Math.abs(stored.saved - saved) >= 0.5) return stored.saved
    } catch {
      return null
    }
    return null
  }

  storeSaved(saved) {
    if (!this.keyValue || document.documentElement.hasAttribute("data-turbo-preview")) return
    try {
      window.localStorage.setItem(this.keyValue, JSON.stringify({ saved, target: this.targetValue }))
    } catch {
      // storage off (private mode): the chart just opens as usual next time
    }
  }

  withNowTotal(total) {
    return this.realMonths.map((m, i) => (i === this.nowIndex ? { ...m, total } : m))
  }

  // The arrival plays only once the chart is on screen (a hidden carousel
  // panel waits); until it plays the old total stays stored, so a visit that
  // never shows the chart plays it next time.
  awaitView() {
    this.viewObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return
      this.viewObserver.disconnect()
      this.arriveTimer = setTimeout(() => this.arrive(), ARRIVE_DELAY_MS)
    }, { threshold: ARRIVE_IN_VIEW })
    this.viewObserver.observe(this.element)
  }

  // A coin arcs in and lands on today's dot; the dot springs up to the new
  // total, the glow a beat late; "+amount" floats up; a milestone passed on
  // the way throws sparks.
  async arrive() {
    if (this.arrival.down) return this.leave()
    const { from, to } = this.arrival
    this.storeSaved(to)
    const [nx, ny] = this.pts.at(-1)
    await coinFly(0, this.screenPoint(Math.max(INSET, nx - COIN_FROM_X), COIN_FROM_Y), this.screenPoint(nx, ny), { ms: COIN_MS, delay: 0, lift: 50 })
    if (this.gone) return
    const host = this.scrollerTarget.parentElement
    anim(this.nowDot, [{ transform: "scale(1.6,.55)" }, { transform: "scale(.85,1.2)", offset: 0.5 }, { transform: "none" }], { duration: 380, easing: "ease-out" })
    floatLabel(host, "gp-plus", this.plotPct(nx, ny - 14), moneyText(Math.round(to - from), this.moneyValue, "+"))
    const now = this.realMonths[this.nowIndex]
    const insFrom = this.insFrom
    const pending = MILESTONE_SHARES.filter((f) => from < this.targetValue * f && to >= this.targetValue * f)
    const sparked = new Set()
    this.shownMax = to
    await tween(SPRING_MS, (p) => {
      if (this.gone) return
      const s = (p * SPRING_MS) / 1000
      const v = from + (to - from) * springAt(s)
      this.months = this.withNowTotal(v)
      this.paintLive({ glowTotal: from + (to - from) * springAt(Math.max(0, s - GLOW_LAG_S)) })
      if (this.readoutIn) this.readoutIn.textContent = moneyText(Math.round(insFrom + Math.min(v, to) - from), this.moneyValue)
      pending.filter((f) => v >= this.targetValue * f && !sparked.has(f)).forEach((f) => {
        sparked.add(f)
        const x = this.crossAt(this.months.slice(0, this.nowIndex + 1), f)
        if (x !== null) burst(host, this.plotPct(x, this.y(this.targetValue * f)), 12)
      })
    }, linear)
    if (this.gone) return
    this.shownMax = null
    this.insFrom = null
    this.months = this.realMonths
    this.paintLive()
    if (this.readoutIn) this.readoutIn.textContent = now.in_text
  }

  // The same show downwards, for a take-out: a red coin pops off today's dot
  // and falls the way the arriving coin came in, mirrored (down to the
  // baseline); the dot squashes and springs DOWN to the new total, the glow a
  // beat late; a red "−amount" floats off it. No milestone sparks: nothing
  // was passed on the way down.
  async leave() {
    const { from, to } = this.arrival
    this.storeSaved(to)
    const [nx, ny] = this.pts.at(-1)
    const host = this.scrollerTarget.parentElement
    const fall = coinFly(0, this.screenPoint(nx, ny), this.screenPoint(Math.max(INSET, nx - COIN_FROM_X), BASE - 4),
      { ms: COIN_MS, delay: 0, lift: 50, className: "gp-coin is-out" })
    // The coin's wind-up (its first ~30%) is it leaving the dot: the drop
    // starts as it lets go.
    await new Promise((resolve) => setTimeout(resolve, COIN_MS * 0.3))
    if (this.gone) return
    anim(this.nowDot, [{ transform: "scale(1.6,.55)" }, { transform: "scale(.85,1.2)", offset: 0.5 }, { transform: "none" }], { duration: 380, easing: "ease-out" })
    floatLabel(host, "gp-plus is-out", this.plotPct(nx, ny - 14), moneyText(Math.round(from - to), this.moneyValue, "−"))
    // The spring overshoots; the dot never dips below the new total (the
    // mirror of shownMax capping the climb at it).
    const at = (s) => Math.max(to, from + (to - from) * springAt(s))
    await Promise.all([fall, tween(SPRING_MS, (p) => {
      if (this.gone) return
      const s = (p * SPRING_MS) / 1000
      this.months = this.withNowTotal(at(s))
      this.paintLive({ glowTotal: at(Math.max(0, s - GLOW_LAG_S)) })
    }, linear)])
    if (this.gone) return
    this.months = this.realMonths
    this.paintLive()
  }

  // A chart point on screen, and as a percentage of the plot (for particles).
  screenPoint(x, y) {
    const rect = this.svg.getBoundingClientRect()
    return [rect.left + x, rect.top + y]
  }

  plotPct(x, y) {
    const [sx, sy] = this.screenPoint(x, y)
    const rect = this.scrollerTarget.parentElement.getBoundingClientRect()
    return [((sx - rect.left) / rect.width) * 100, ((sy - rect.top) / rect.height) * 100]
  }

  get reduced() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  x(i) { return this.padL + i * this.step }
  y(v) { return BASE - (Math.min(v, this.targetValue) / this.targetValue) * (BASE - TOP) }

  // Redraw at a new width (the card was hidden at connect, or rotated).
  clear() {
    this.scrollerTarget.replaceChildren()
    this.scrollerTarget.parentElement.querySelectorAll(".gc-pin, .gc-readout, .gc-rail-cap, .gc-cue").forEach((node) => node.remove())
  }

  rebuild() {
    this.clear()
    const selected = this.selected
    this.build()
    if (selected !== undefined) this.select(selected)
  }

  build() {
    const months = this.months
    const n = months.length
    // Each month's tile is its share of the card (gaps between, an inset at
    // each end); once that would be narrower than MIN_TILE the tiles keep
    // MIN_TILE and the months scroll. The dot sits on the tile's centre, so a
    // month is one clean column top to bottom.
    const avail = this.scrollerTarget.clientWidth || 320
    this.tileW = Math.max(MIN_TILE, (avail - 2 * INSET - GAP * (n - 1)) / n)
    this.step = this.tileW + GAP
    this.padL = INSET + this.tileW / 2
    const width = 2 * this.padL + this.step * Math.max(n - 1, 0)
    const height = TILE_TOP + TILE_H + 2
    const svg = this.el("svg", { width, height, viewBox: `0 0 ${width} ${height}` }, this.scrollerTarget)
    const defs = this.el("defs", {}, svg)
    this.gradients(defs)
    this.defs = defs
    this.svg = svg

    this.baseline = this.el("line", { x1: 0, x2: width, y1: BASE, y2: BASE, stroke: "var(--gc-base)" }, svg)
    // The selected month's column: from the baseline up to its dot, the dot
    // sitting in its rounded top; square bottom (runs past the baseline, cut there)
    const bandClip = this.el("clipPath", { id: "gcBandClip" }, defs)
    this.el("rect", { x: 0, y: 0, width, height: BASE }, bandClip)
    this.band = this.el("rect", { class: "gc-band", "clip-path": "url(#gcBandClip)", x: this.x(this.nowIndex) - BAND_W / 2, y: BASE, width: BAND_W, height: 0, rx: BAND_W / 2 }, svg)
    // Everything today's total moves sits in these layers (paintLive), so a
    // new set-aside can repaint them alone: the rail and milestone stems
    // behind the band; the line, wash, road and milestone shapes over it;
    // the dots over the selection glow; today's dot on top of those.
    this.liveBack = this.el("g", {})
    svg.insertBefore(this.liveBack, this.band)
    this.liveMid = this.el("g", {}, svg)

    // the selected dot's glow sits under the dots
    this.glow = this.el("g", {}, svg)
    this.halo = this.el("circle", { r: 16 }, this.glow)
    this.ring = this.el("circle", { class: "gc-pulse", r: 9 }, this.glow)
    this.glow.style.opacity = 0
    this.liveFront = this.el("g", {}, svg)
    this.nowLayer = this.el("g", {}, svg)
    this.nowDot = null
    this.flagEl = null
    this.capEl = null
    this.paintLive()

    const gx = this.x(n - 1)
    const gy = this.y(this.targetValue)
    this.trophy(gx, gy)
    this.flag(months[0].outs > 0)
    this.tiles(months)
    // The selected month's numbers, pinned on the left (lined up with the
    // rail's Progress cap), centred between the rail and the top of the start
    // flag; it stays put, only its rows change.
    const flagTop = this.pts[0][1] - FLAG_H * FLAG_SCALE
    this.readout = document.createElement("div")
    this.readout.className = "gc-readout"
    this.readoutCentre = (MARK_TOP + flagTop) / 2
    this.readout.style.top = `${this.readoutCentre}px`
    this.scrollerTarget.parentElement.appendChild(this.readout)
    this.pin(gx, gy)
    this.cues()
  }

  // The parts that follow today's total: the line (wash, glow, red take-out
  // bends), the road ahead and its dots, the milestones and the rail, the
  // month dots. glowTotal lets the glow trail the line while it moves.
  paintLive({ glowTotal } = {}) {
    const months = this.months
    const n = months.length
    ;[this.liveBack, this.liveMid, this.liveFront].forEach((layer) => layer.replaceChildren())
    const mid = this.liveMid
    const front = this.liveFront
    const done = months.slice(0, this.nowIndex + 1)
    const pts = done.map((m, i) => [this.x(i), this.y(m.total || 0)])
    this.pts = pts
    const seg = this.monotone(pts)
    const d = this.pathD(pts, seg)
    const glowPts = glowTotal === undefined ? pts : [...pts.slice(0, -1), [pts.at(-1)[0], this.y(glowTotal)]]
    this.el("path", { class: "gc-fade", d: `${d} L${pts.at(-1)[0]},${BASE} L${pts[0][0]},${BASE} Z`, fill: "url(#gcWash)" }, mid)
    const glow = this.el("g", { opacity: 0.14 }, mid)
    this.el("path", { class: "gc-fade", d: this.pathD(glowPts, this.monotone(glowPts)), fill: "none", stroke: "var(--gc-save)", "stroke-width": 12, "stroke-linecap": "round" }, glow)
    const line = this.el("path", { class: "gc-draw", d, fill: "none", stroke: "var(--gc-save)", "stroke-width": 3.5, "stroke-linecap": "round" }, mid)
    line.style.setProperty("--len", Math.ceil(line.getTotalLength?.() || 3000) + 2)
    pts.forEach((p, i) => {
      if (!i || !(done[i].outs > 0)) return
      const red = this.el("path", { class: "gc-draw", d: `M${pts[i - 1][0]},${pts[i - 1][1]} ${seg(i - 1)}`, fill: "none", stroke: "var(--gc-out)", "stroke-width": 2.5, "stroke-linecap": "round" }, mid)
      red.style.setProperty("--len", Math.ceil(red.getTotalLength?.() || 400) + 2)
    })

    // Months ahead: the plan (GoalMonths#with_plan), grey dots on a grey
    // dotted path from today to the goal, each one tappable.
    const planned = months.some((m, i) => i > this.nowIndex && m.total != null)
    const ahead = planned ? [pts[this.nowIndex], [this.x(n - 1), this.y(this.targetValue)]] : []
    this.allPts = months.map((m, i) => (i <= this.nowIndex ? pts[i] : null))
    this.roadAt = null
    if (ahead.length > 1) {
      // A soft S from today to the coin: leaves today flat, rises, eases in.
      const [[ax, ay], [bx, by]] = ahead
      const c1 = [ax + (bx - ax) * 0.45, ay]
      const c2 = [ax + (bx - ax) * 0.55, by]
      const road = `M${ax},${ay} C${c1[0]},${c1[1]} ${c2[0]},${c2[1]} ${bx},${by}`
      this.el("path", { class: "gc-fade", d: road, fill: "none", stroke: "var(--gc-grey)", "stroke-width": 2, "stroke-dasharray": "2 6", "stroke-linecap": "round" }, mid)
      // each month ahead sits on the curve at its own x
      const at = (t) => {
        const u = 1 - t
        return [u * u * u * ax + 3 * u * u * t * c1[0] + 3 * u * t * t * c2[0] + t * t * t * bx,
          u * u * u * ay + 3 * u * u * t * c1[1] + 3 * u * t * t * c2[1] + t * t * t * by]
      }
      for (let i = this.nowIndex + 1; i < n; i++) {
        const target = this.x(i)
        let lo = 0
        let hi = 1
        for (let k = 0; k < 24; k++) { const mid = (lo + hi) / 2; if (at(mid)[0] < target) lo = mid; else hi = mid }
        this.allPts[i] = at((lo + hi) / 2)
      }
      this.roadAt = at
    }
    this.milestones(done)

    this.allPts.forEach((p, i) => {
      if (!p || i <= this.nowIndex || i === n - 1) return
      this.el("circle", { class: "gc-pop", style: `animation-delay:${0.6 + i * 0.05}s`, cx: p[0], cy: p[1], r: 4, fill: "var(--gc-grey-dot)", stroke: "var(--color-base-200)", "stroke-width": 2 }, front)
    })
    this.allPts.forEach((p, i) => {
      if (!p || i <= this.nowIndex) return
      const hit = this.el("circle", { cx: p[0], cy: p[1], r: 18, fill: "transparent", style: "cursor:pointer" }, front)
      hit.addEventListener("click", () => this.select(i))
    })
    pts.forEach(([px, py], i) => {
      if (i === this.nowIndex) return
      this.dot(i, px, py, front)
    })
    const gx = this.x(n - 1)
    const gy = this.y(this.targetValue)
    const [nx, ny] = pts.at(-1)
    if (n - 1 > this.nowIndex && ahead.length < 2) {
      this.el("line", { class: "gc-fade", x1: nx, y1: ny, x2: gx, y2: gy, stroke: "rgba(252,211,77,.3)", "stroke-width": 2, "stroke-dasharray": "1 6", "stroke-linecap": "round" }, front)
    }
    // Today's dot lives on its own layer and is moved, not redrawn, so its
    // landing squash survives the repaints that follow.
    if (this.nowDot) {
      ;[this.nowDot, this.nowHit].forEach((c) => { c.setAttribute("cx", nx); c.setAttribute("cy", ny) })
    } else {
      [this.nowDot, this.nowHit] = this.dot(this.nowIndex, nx, ny, this.nowLayer)
    }
    if (this.flagEl) this.flagEl.parentElement.setAttribute("transform", `translate(0 ${pts[0][1] - this.flagY})`)
    if (this.selected === this.nowIndex) this.placeSelection(nx, ny)
  }

  // A month's dot (red when it had a take-out) with its tap target.
  dot(i, px, py, parent) {
    const dot = this.el("circle", { class: "gc-pop", style: `animation-delay:${0.35 + i * 0.08}s`, cx: px, cy: py, r: i === this.nowIndex ? 6 : 4,
      fill: this.months[i].outs > 0 ? "var(--gc-out)" : "var(--gc-save)", stroke: "var(--color-base-200)", "stroke-width": 2 }, parent)
    const hit = this.el("circle", { cx: px, cy: py, r: 18, fill: "transparent", style: "cursor:pointer" }, parent)
    hit.addEventListener("click", () => this.select(i))
    return [dot, hit]
  }

  pathD(pts, seg) {
    return pts.length > 1 ? `M${pts[0][0]},${pts[0][1]} ` + pts.slice(1).map((_, i) => seg(i)).join(" ") : `M${pts[0][0]},${pts[0][1]}`
  }

  // Dashed vertical lines where the line crosses 25%, 50% and 75% of the
  // goal (done, or on the planned road) and at the goal, each labelled on
  // top, with a gold shape on the line at each crossing.
  milestones(done) {
    const layer = this.el("g", { class: "gc-fade" }, this.liveBack)
    const marks = [[0.25, "25%", "diamond"], [0.5, "50%", "star"], [0.75, "75%", "hex"]]
      .map(([f, text, shape]) => ({ x: this.crossAt(done, f), text, shape, f }))
    marks.push({ x: this.x(this.months.length - 1), text: this.labelsValue.goal || "", goal: true, f: 1 })
    const pct = this.rail(layer, marks)
    const shapes = this.el("g", {}, this.liveMid)
    // Labels that would touch: crossings can bunch up (a big month crosses
    // 25, 50 and 75% at once). From the goal back, a label shows only with
    // room from the last one shown; its stem and shape stay either way.
    let lastX = Infinity
    marks.filter((m) => m.x != null).sort((a, b) => b.x - a.x).forEach((m) => {
      m.labelled = lastX - m.x >= LABEL_GAP
      if (m.labelled) lastX = m.x
    })
    marks.forEach(({ x, text, shape, f, goal, labelled }) => {
      if (x == null) return
      // A stem from the rail down to the milestone's own shape (the trophy for
      // the goal), nothing below it; gold once reached.
      const reached = pct >= (goal ? 100 : f * 100)
      // A share already passed by the first month sits on the start point
      // (where the line begins), not below it at its own level.
      const level = goal ? this.y(this.targetValue) : this.y(this.targetValue * f)
      const sy = !goal && x === this.x(0) ? Math.min(level, this.pts[0][1]) : level
      const end = goal ? level - TROPHY_TOP : sy - SHAPE_GAP
      const cls = goal || reached ? "gc-mark-line is-gold" : "gc-mark-line"
      if (end > MARK_TOP) this.el("line", { class: cls, x1: x, x2: x, y1: MARK_TOP, y2: end }, layer)
      this.el("circle", { class: cls, cx: x, cy: MARK_TOP + 1, r: 2 }, layer)
      if (labelled) {
        const label = this.el("text", { x, y: RAIL_Y, "text-anchor": "middle", "dominant-baseline": "central", class: goal ? "gc-mark is-goal" : "gc-mark" }, layer)
        label.textContent = text
        if (reached) label.classList.add("is-reached")
      }
      if (shape) {
        const reached = done.some((m) => (m.total || 0) >= this.targetValue * f)
        this.el("path", { class: "gc-pop", style: "animation-delay:1.4s", d: this.shape(shape, x, sy), opacity: reached ? 1 : 0.5,
          fill: "var(--gc-gold)", stroke: "#A16207", "stroke-width": 1.2, "stroke-linejoin": "round" }, shapes)
      }
    })
  }

  // The progress rail: a soft track across the whole chart behind the
  // milestone labels, filled green up to today; its left end is pinned
  // (Progress and the percentage saved) while the months scroll under it.
  // The fill reaches today, and always far enough to sit under every reached
  // milestone's whole label (a label is centred on its crossing, which can be
  // today itself).
  rail(layer, marks) {
    const width = +this.svg.getAttribute("width")
    // while the dot springs past the new total the % never reads past it
    const total = Math.min(this.months[this.nowIndex]?.total || 0, this.shownMax ?? Infinity)
    const pct = Math.max(0, Math.floor((total / this.targetValue) * 100))
    const reachedX = marks.filter((m) => m.x != null && pct >= m.f * 100).map((m) => m.x + RAIL_LABEL_HALF)
    const nowX = Math.min(width, Math.max(this.pts.at(-1)[0], ...reachedX))
    const top = RAIL_Y - RAIL_H / 2
    this.el("rect", { class: "gc-rail", x: 0, y: top, width, height: RAIL_H, rx: RAIL_H / 2 }, layer)
    this.el("rect", { class: "gc-rail-fill", x: 0, y: top, width: nowX, height: RAIL_H, rx: RAIL_H / 2, fill: "url(#gcRailFill)" }, layer)

    if (!this.capEl) {
      const cap = document.createElement("div")
      cap.className = "gc-rail-cap"
      cap.style.top = `${top + RAIL_H / 2 - 10}px`
      const name = document.createElement("span")
      name.textContent = this.labelsValue.progress || ""
      this.capValue = document.createElement("b")
      cap.append(name, this.capValue)
      this.scrollerTarget.parentElement.appendChild(cap)
      this.capEl = cap
    }
    this.capValue.textContent = `${pct}%`
    requestAnimationFrame(() => this.syncCapLabels())
    return pct
  }

  // Milestone labels scrolled under the pinned Progress cap hide, so none
  // shows through its fade ("57% %").
  syncCapLabels() {
    const cap = this.scrollerTarget.parentElement.querySelector(".gc-rail-cap")
    if (!cap || !this.svg) return
    const edge = cap.offsetWidth - CAP_FADE + this.scrollerTarget.scrollLeft
    this.svg.querySelectorAll("text.gc-mark").forEach((label) => {
      label.style.opacity = +label.getAttribute("x") - LABEL_HALF < edge ? 0 : ""
    })
  }

  // Where the line reaches a share of the goal: between the two months it
  // crossed in, or on the planned road; null when neither gets there.
  crossAt(done, f) {
    const goal = this.targetValue * f
    const i = done.findIndex((m) => (m.total || 0) >= goal)
    if (i === 0) return this.x(0)
    if (i > 0) return this.x(i - 1) + ((goal - done[i - 1].total) / (done[i].total - done[i - 1].total)) * this.step
    if (!this.roadAt) return null
    const gy = this.y(goal)
    let lo = 0
    let hi = 1
    for (let k = 0; k < 24; k++) { const mid = (lo + hi) / 2; if (this.roadAt(mid)[1] > gy) lo = mid; else hi = mid }
    return this.roadAt((lo + hi) / 2)[0]
  }

  // 25% a diamond, 50% a star, 75% a hexagon, centred on (x, y).
  shape(kind, x, y, scale = 1) {
    const ring = (count, radius) => Array.from({ length: count }, (_, k) => {
      const r = radius(k) * scale
      const a = -Math.PI / 2 + (k * 2 * Math.PI) / count
      return `${k ? "L" : "M"}${(x + r * Math.cos(a)).toFixed(2)},${(y + r * Math.sin(a)).toFixed(2)}`
    }).join(" ") + " Z"
    if (kind === "star") return ring(10, (k) => (k % 2 ? 3.6 : 8))
    if (kind === "hex") return ring(6, () => 6.5)
    return `M${x},${y - 7} L${x + 6},${y} L${x},${y + 7} L${x - 6},${y} Z`
  }

  // The start: a sticker flag planted on the first point, tilted, cloth
  // rippling; red when that first month had a take-out.
  flag(out) {
    const [sx, sy] = this.pts[0]
    const [fill, edge] = out ? ["#F87171", "#B91C1C"] : ["#22C55E", "#15803D"]
    // Slaps on again whenever its month is picked (syncEnds).
    // Both live in one group, moved with the first point (paintLive).
    const wrap = this.el("g", {}, this.svg)
    this.flagY = sy
    this.flagEl = this.el("g", { class: "gc-flag" }, wrap)
    this.flagSlap = this.pivot("gc-slap", sx, sy, this.flagEl)
    const tilt = this.el("g", { transform: `rotate(-6 ${sx} ${sy}) translate(${sx} ${sy}) scale(${FLAG_SCALE}) translate(${-sx} ${-sy})` }, this.flagSlap)
    const cloth = `M${sx + 1.5},${sy - 36} C${sx + 9},${sy - 41} ${sx + 16},${sy - 31} ${sx + 23.5},${sy - 36} L${sx + 23.5},${sy - 21} C${sx + 16},${sy - 16} ${sx + 9},${sy - 26} ${sx + 1.5},${sy - 21} Z`
    this.el("line", { x1: sx, y1: sy, x2: sx, y2: sy - 38, stroke: CREAM, "stroke-width": 8, "stroke-linecap": "round" }, tilt)
    this.el("path", { class: "gc-wave", d: cloth, fill: CREAM, stroke: CREAM, "stroke-width": 7, "stroke-linejoin": "round", style: `transform-origin:${sx}px ${sy - 28}px` }, tilt)
    this.el("line", { x1: sx, y1: sy, x2: sx, y2: sy - 38, stroke: "#475569", "stroke-width": 3, "stroke-linecap": "round" }, tilt)
    this.el("path", { class: "gc-wave", d: cloth, fill, stroke: edge, "stroke-width": 2, "stroke-linejoin": "round", style: `transform-origin:${sx}px ${sy - 28}px` }, tilt)
    this.el("circle", { cx: sx, cy: sy, r: 6.5, fill, stroke: CREAM, "stroke-width": 3 }, wrap)
    this.flagEl.addEventListener("click", () => this.select(0))
  }

  // Picking the first month slaps the flag on again, picking the goal month
  // the trophy; only on a change of month, not on the opening selection.
  syncEnds(i) {
    if (this.selected === undefined || this.selected === i || this.reduced) return
    if (i === 0) this.replay(this.flagSlap)
    if (i === this.months.length - 1) this.replay(this.trophySlap)
  }

  replay(slap) {
    if (!slap) return
    slap.style.animation = "none"
    void slap.getBoundingClientRect()
    slap.style.animation = "gcSlap .7s both"
  }

  // The goal: a chunky gold sticker trophy that slaps on, then every few
  // seconds leans back and springs into a cheer with sparkles.
  trophy(gx, gy) {
    // Slaps on again whenever its month is picked, like the flag.
    this.el("circle", { class: "gc-shine", cx: gx, cy: gy, r: 30, fill: "url(#gcHalo)" }, this.svg)
    const slap = this.pivot("gc-slap gc-end", gx, gy + 18)
    this.trophySlap = slap
    slap.addEventListener("click", () => this.select(this.months.length - 1))
    const cheer = this.pivot("gc-cheer", gx, gy + 18, slap)
    const tilt = this.el("g", { transform: `rotate(7 ${gx} ${gy + 18})` }, cheer)
    const body = this.el("g", { transform: `translate(${gx} ${gy}) scale(1) translate(${-gx} ${-gy})` }, tilt)
    const cup = `M${gx - 11},${gy - 13} H${gx + 11} V${gy - 5} C${gx + 11},${gy + 4} ${gx + 6},${gy + 8} ${gx},${gy + 8} C${gx - 6},${gy + 8} ${gx - 11},${gy + 4} ${gx - 11},${gy - 5} Z`
    const handles = `M${gx - 11},${gy - 10} C${gx - 19},${gy - 10} ${gx - 19},${gy + 1} ${gx - 8},${gy + 2} M${gx + 11},${gy - 10} C${gx + 19},${gy - 10} ${gx + 19},${gy + 1} ${gx + 8},${gy + 2}`
    const border = this.el("g", { fill: CREAM, stroke: CREAM, "stroke-width": 7, "stroke-linejoin": "round", "stroke-linecap": "round" }, body)
    this.el("path", { d: handles, fill: "none", "stroke-width": 11 }, border)
    this.el("path", { d: cup }, border)
    this.el("rect", { x: gx - 10, y: gy + 7, width: 20, height: 11, rx: 2.5 }, border)
    this.el("path", { d: handles, fill: "none", stroke: GOLD.edge, "stroke-width": 6, "stroke-linecap": "round" }, body)
    this.el("path", { d: handles, fill: "none", stroke: GOLD.shade, "stroke-width": 2.4, "stroke-linecap": "round" }, body)
    this.el("rect", { x: gx - 3, y: gy + 7, width: 6, height: 6, fill: GOLD.shade, stroke: GOLD.edge, "stroke-width": 2.5 }, body)
    this.el("rect", { x: gx - 10, y: gy + 12, width: 20, height: 6, rx: 2.5, fill: GOLD.shade, stroke: GOLD.edge, "stroke-width": 2.5 }, body)
    this.el("path", { d: cup, fill: GOLD.fill, stroke: GOLD.edge, "stroke-width": 2.5, "stroke-linejoin": "round" }, body)
    this.el("path", { d: `M${gx + 4},${gy - 13} H${gx + 11} V${gy - 5} C${gx + 11},${gy + 4} ${gx + 6},${gy + 8} ${gx + 1},${gy + 8} C${gx + 5},${gy + 3} ${gx + 5},${gy - 6} ${gx + 4},${gy - 13} Z`, fill: GOLD.shade }, body)
    this.el("path", { d: `M${gx - 6},${gy - 9} L${gx - 6},${gy - 2}`, stroke: GOLD.hi, "stroke-width": 2.6, "stroke-linecap": "round" }, body)
    this.el("path", { d: this.shape("star", gx + 0.5, gy - 3, 0.55), fill: GOLD.hi, stroke: GOLD.edge, "stroke-width": 1, "stroke-linejoin": "round" }, body)
    ;[[-22, -16, 5, 0], [21, -20, 4, 0.15], [24, 4, 3.5, 0.3]].forEach(([dx, dy, r, delay]) => {
      const q = r * 0.32
      const x = gx + dx
      const y = gy + dy
      this.el("path", { class: "gc-spark", style: `animation-delay:${2.6 + delay}s`, fill: "#fff",
        d: `M${x},${y - r} L${x + q},${y - q} L${x + r},${y} L${x + q},${y + q} L${x},${y + r} L${x - q},${y + q} L${x - r},${y} L${x - q},${y - q} Z` }, this.svg)
    })
  }

  // A group that animates around a fixed point (a pole's foot, a base).
  pivot(cls, x, y, parent = this.svg) {
    return this.el("g", { class: cls, style: `transform-box:view-box;transform-origin:${x}px ${y}px` }, parent)
  }

  // One tile per month: its name over a thin bar, green then red by share;
  // a grey bar for a past month with nothing in it, four grey dots for a
  // month still to come.
  tiles(months) {
    this.tileEls = months.map((m, i) => {
      const g = this.el("g", { class: "gc-tile" }, this.svg)
      const tx = this.x(i) - this.tileW / 2
      const cx = this.x(i)
      const moved = m.ins + m.outs
      const filled = m.state !== "future" && moved > 0
      if (filled) g.classList.add("has")
      this.el("rect", { x: tx, y: TILE_TOP, width: this.tileW, height: TILE_H, fill: "transparent" }, g) // the tap area: the whole month
      const label = this.el("text", { x: cx, y: TILE_TOP + 15, "text-anchor": "middle", "dominant-baseline": "central", class: "gc-month" }, g)
      label.textContent = m.label
      // The selected surface: square top against the baseline, rounded
      // bottom; its side padding around the month name 30% under the tile's.
      const textW = Math.min(label.getComputedTextLength?.() || 0, this.tileW)
      const w = this.tileW - ((this.tileW - textW) / 2) * 2 * (1 - TILE_PAD)
      const sx = cx - w / 2
      g.insertBefore(this.el("path", { class: "gc-hit", d: `M${sx},${TILE_TOP} H${sx + w} V${TILE_TOP + TILE_H - 12} A12 12 0 0 1 ${sx + w - 12},${TILE_TOP + TILE_H} H${sx + 12} A12 12 0 0 1 ${sx},${TILE_TOP + TILE_H - 12} Z` }), label)
      const bar = this.el("g", { class: "gc-bar" }, g)
      const bw = (this.tileW - 24) * BAR_W // the green/red bar, centred under the name
      const bx = cx - bw / 2
      const by = TILE_TOP + 27
      if (filled) {
        const outW = m.outs > 0 ? Math.max(4, Math.round(bw * (m.outs / moved))) : 0
        this.el("rect", { x: bx, y: by, width: bw - outW - (outW ? 2 : 0), height: 4, rx: 2, fill: "var(--gc-save-hi)" }, bar)
        if (outW) this.el("rect", { x: bx + bw - outW, y: by, width: outW, height: 4, rx: 2, fill: "var(--gc-out)" }, bar)
      } else if (m.state === "future") {
        for (let k = 0; k < 4; k++) this.el("circle", { cx: cx + (k - 1.5) * 7, cy: by + 2, r: 1.6, fill: "var(--gc-grey)" }, bar)
      } else {
        this.el("rect", { x: bx, y: by, width: bw, height: 4, rx: 2, fill: "var(--gc-grey)" }, bar)
      }
      g.addEventListener("click", () => this.select(i))
      return g
    })
  }

  pin(gx, gy) {
    const pin = document.createElement("button")
    pin.type = "button"
    pin.className = "gc-pin"
    pin.setAttribute("aria-label", this.labelsValue.goal || "")
    pin.style.top = `${gy - 11}px`
    pin.addEventListener("click", () => this.scrollerTarget.scrollTo({ left: this.scrollerTarget.scrollWidth, behavior: "smooth" }))
    this.scrollerTarget.parentElement.appendChild(pin)
    this.pinEl = pin
    this.goalX = gx
  }

  // Swipe cues on the month row: the About page's bouncing arrow on its side,
  // small and bare (.gc-cue). One at each end, shown only while there are
  // months to scroll to that way; none when everything fits.
  cues() {
    const arrow = '<svg viewBox="0 0 24 24"><path d="M9 6l6 6-6 6"/></svg>'
    this.cueEls = ["is-left", "is-right"].map((side) => {
      const cue = document.createElement("span")
      cue.className = `gc-cue ${side}`
      cue.setAttribute("aria-hidden", "true")
      cue.style.top = `${TILE_TOP}px`
      cue.style.height = `${TILE_H}px`
      cue.innerHTML = `<span class="gc-cue-arrow">${arrow.replace("<svg", '<svg class="gc-cue-ghost"')}${arrow.replace("<svg", '<svg class="gc-cue-lead"')}</span>`
      this.scrollerTarget.parentElement.appendChild(cue)
      return cue
    })
  }

  syncCues() {
    if (!this.cueEls) return
    const scroller = this.scrollerTarget
    const max = scroller.scrollWidth - scroller.clientWidth
    const left = scroller.scrollLeft
    this.cueEls[0].classList.toggle("is-on", max > 2 && left > 2)
    this.cueEls[1].classList.toggle("is-on", max > 2 && left < max - 2)
  }

  // The pinned coin only while the goal's coin is scrolled out of view.
  syncPin() {
    if (!this.pinEl) return
    const width = this.scrollerTarget.clientWidth
    const hidden = width > 0 && this.goalX + 14 - this.scrollerTarget.scrollLeft > width
    this.pinEl.classList.toggle("is-on", hidden)
  }

  // A month: give its tile a surface, glow its dot, fill the readout.
  select(i) {
    if (!this.allPts || !this.allPts[i]) return
    const m = this.months[i]
    const [px, py] = this.allPts[i]
    const ahead = i > this.nowIndex
    this.tileEls.forEach((t, k) => t.classList.toggle("is-sel", k === i))
    this.syncEnds(i)

    const tone = ahead ? "148,163,184" : (m.outs > 0 ? "248,113,113" : "52,211,153")
    this.selected = i
    this.placeSelection(px, py)
    this.halo.setAttribute("fill", `rgba(${tone},.22)`)
    this.ring.setAttribute("fill", `rgba(${tone},.55)`)
    this.glow.style.opacity = 1
    if (!this.reduced) {
      this.glow.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 300, easing: "ease-out" })
    }

    this.readout.replaceChildren()
    this.readoutIn = null
    if (ahead) {
      this.line("is-ahead", this.labelsValue.expected, m.expected_text)
    } else {
      // Today before its new money lands reads what it had (arrive counts it up).
      const pending = i === this.nowIndex && this.insFrom != null
      const value = this.line("is-in", this.labelsValue.set_aside, pending ? moneyText(Math.round(this.insFrom), this.moneyValue) : m.in_text)
      if (i === this.nowIndex) this.readoutIn = value
      if (m.outs > 0) this.line("is-out", this.labelsValue.taken_out, m.out_text)
    }
    // Never up into the rail: a high start (a goal reached in a month or
    // two) pushes the centre down by however tall the rows came out.
    const minCentre = MARK_TOP + READOUT_GAP + this.readout.offsetHeight / 2
    this.readout.style.top = `${Math.max(this.readoutCentre, minCentre)}px`
    this.readout.classList.remove("is-on")
    void this.readout.offsetWidth
    this.readout.classList.add("is-on")
  }

  // The selected month's column up to its dot, and the glow on the dot.
  placeSelection(px, py) {
    ;[this.halo, this.ring].forEach((c) => { c.setAttribute("cx", px); c.setAttribute("cy", py) })
    const top = Math.min(py - BAND_W / 2, BASE)
    this.band.setAttribute("x", px - BAND_W / 2)
    this.band.setAttribute("y", top)
    this.band.setAttribute("height", BASE - top + BAND_W)
  }

  line(cls, label, amount) {
    const row = document.createElement("span")
    row.className = cls
    const name = document.createElement("i")
    name.textContent = label
    const value = document.createElement("b")
    value.textContent = amount
    row.append(name, value)
    this.readout.append(row)
    return value
  }

  gradients(defs) {
    const grad = (tag, id, attrs, stops) => {
      const g = this.el(tag, { id, ...attrs }, defs)
      stops.forEach(([offset, color]) => this.el("stop", { offset, "stop-color": color }, g))
    }
    grad("linearGradient", "gcWash", { x1: 0, x2: 0, y1: 0, y2: 1 }, [[0, "rgba(15,166,120,.32)"], [1, "rgba(15,166,120,0)"]])
    grad("linearGradient", "gcRailFill", { x1: 0, x2: 1, y1: 0, y2: 0 }, [[0, "rgba(15,166,120,.10)"], [1, "rgba(52,211,153,.45)"]])
    grad("radialGradient", "gcHalo", {}, [[0, "rgba(252,211,77,.55)"], [1, "rgba(252,211,77,0)"]])
  }

  // Monotone cubic (Fritsch–Carlson): smooth, never overshoots between months.
  monotone(pts) {
    const n = pts.length
    const d = []
    const m = []
    for (let i = 0; i < n - 1; i++) d.push((pts[i + 1][1] - pts[i][1]) / (pts[i + 1][0] - pts[i][0]))
    m[0] = d[0] ?? 0
    m[n - 1] = d[n - 2] ?? 0
    for (let i = 1; i < n - 1; i++) m[i] = d[i - 1] * d[i] <= 0 ? 0 : (d[i - 1] + d[i]) / 2
    return (i) => {
      const [x0, y0] = pts[i]
      const [x1, y1] = pts[i + 1]
      const h = (x1 - x0) / 3
      return `C${x0 + h},${y0 + m[i] * h} ${x1 - h},${y1 - m[i + 1] * h} ${x1},${y1}`
    }
  }

  el(tag, attrs, parent) {
    const node = document.createElementNS(NS, tag)
    Object.entries(attrs).forEach(([k, v]) => node.setAttribute(k, v))
    if (parent) parent.appendChild(node)
    return node
  }
}
